import React, { useEffect, useRef, useState } from 'react'
import * as XLSX from "xlsx";
import * as selector from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import ButtonComponent from '../shared/button/button.component';
import { ContactMatchIcon, MergeContactIcon, ShieldIcon, TickBlueIcon, UploadFileBlueIcon } from '../../icon';
import { FileService } from '../../services/FileService';
import Config from '../../config';

/**
 * For phone cells, prefer the raw stored value. Excel "General" format often sets `w` to
 * scientific notation (e.g. 1.23457E+12); re-parsing that string loses digits. `v` holds
 * the exact IEEE value (safe for E.164 lengths under Number.MAX_SAFE_INTEGER).
 */
const getPhoneCellExactText = (cell: any, wb: any): string => {
    if (!cell) return "";
    if (cell.t === "n" && typeof cell.v === "number" && Number.isFinite(cell.v)) {
        const v = cell.v;
        if (Number.isInteger(v) && Math.abs(v) <= Number.MAX_SAFE_INTEGER) {
            return String(v);
        }
        return String(v);
    }
    if (cell.t === "s") {
        if (typeof cell.v === "string") return cell.v.trim();
        if (typeof cell.v === "number" && Array.isArray(wb?.Strings) && wb.Strings[cell.v] != null) {
            const entry = wb.Strings[cell.v] as any;
            if (typeof entry === "string") return entry.trim();
            if (entry?.t) return String(entry.t).trim();
        }
    }
    if (cell.t === "str" && cell.v != null) return String(cell.v).trim();
    if (cell.w != null && String(cell.w).trim() !== "") return String(cell.w).trim();
    if (cell.v != null && cell.v !== "") return String(cell.v).trim();
    return "";
};

const MAX_IMPORT_FILE_SIZE_BYTES = 25 * 1024 * 1024;

interface Iprops {
    HandleUploadFileData?: (file: File, jsonData: Record<string, string>[]) => void;
    UploadedFile: File | undefined;
    HandleClearFile: () => void
}

const ImportFile = (props: Iprops) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const IsDarkMode: boolean = useSelector(selector.isDarkMode)

    const [fileUploadError, setFileUploadError] = useState<string>("")
    const [uploadedFile, setUploadedFile] = useState<File | undefined>(props?.UploadedFile);

    useEffect(() => {
        setUploadedFile(props?.UploadedFile)
    }, [props?.UploadedFile])


    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Normalize
    const normalize = (str: string) =>
        str.toLowerCase().replace(/\./g, "").replace(/[^a-z0-9]/g, "");

    const normalizePhoneNumber = (value: any): string => {
        if (!value) return "";

        let str = value.toString().trim();

        // handle scientific notation (E+)
        if (/e\+/i.test(str)) {
            const num = Number(str);
            if (!isNaN(num)) {
                return num.toLocaleString("fullwide", { useGrouping: false });
            }
        }

        return str;
    };

    const handleOpenFileManager = () => {
        fileInputRef.current?.click();
    };

    const handleClearFile = () => {
        setUploadedFile(undefined);
        setFileUploadError("");
        resetFileInput();
        props?.HandleClearFile();
    };

    const handleFileUpload = (file: File | undefined) => {
        if (!file) return;

        if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
            setFileUploadError("File size should not exceed 25 MB");
            return;
        }

        setFileUploadError("");

        const reader = new FileReader();
        const isCSV = file.name.toLowerCase().endsWith(".csv");
        if (isCSV) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
        reader.onload = (event: any) => {
            let workbook;

            if (isCSV) {
                workbook = XLSX.read(event.target.result, { type: "string", cellText: true });
            } else {
                const data = new Uint8Array(event.target.result);
                workbook = XLSX.read(data, { type: "array", cellText: true });
            }
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, {
                defval: "",
                raw: false
            });
            // check rows available
            if (!jsonData || jsonData.length === 0) {
                setFileUploadError("file contains no data.");
                return;
            }

            if (jsonData?.length >= 20000) {
                setFileUploadError("file contains more then 20000 rows");
                return;
            }

            // check at least one valid row data
            const hasValidRow = jsonData.some((row: any) =>
                Object.values(row).some(
                    (cell) => cell?.toString().trim() !== ""
                )
            );

            if (!hasValidRow) {
                setFileUploadError("At least one row with data is required.");
                return;
            }

            // check required headers
            const headers = Object.keys(jsonData[0]).map((x) => normalize(x));

            const hasFirstName = headers.includes("firstname");

            const hasPhoneNumber =
                headers.includes("phonenumber") ||
                headers.includes("mobile") ||
                headers.includes("mobilenumber");

            let errorMessage = "";

            if (!hasFirstName) {
                errorMessage += "Firstname column missing. ";
            }

            if (!hasPhoneNumber) {
                errorMessage += "Phonenumber column missing.";
            }

            if (errorMessage) {
                setFileUploadError(errorMessage);
                return;
            }
            //find phone number column
            const phoneHeader = Object.keys(jsonData[0]).find((header) => {
                const normalized = normalize(header);

                return (
                    normalized === "phonenumber" ||
                    normalized === "mobile" ||
                    normalized === "mobilenumber"
                );
            });

            // Normalize phones: take exact value from sheet cells (numeric `v`, not formatted `w`), then strip formatting, etc.
            if (phoneHeader) {
                const ref = worksheet["!ref"];
                const range = ref
                    ? XLSX.utils.decode_range(ref)
                    : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
                const headerKeys = Object.keys(jsonData[0]);
                const colOffset = headerKeys.indexOf(phoneHeader);
                const phoneCol = colOffset >= 0 ? range.s.c + colOffset : -1;

                jsonData.forEach((row: any, i: number) => {
                    if (phoneCol >= 0) {
                        const addr = XLSX.utils.encode_cell({ r: range.s.r + 1 + i, c: phoneCol });
                        const exact = getPhoneCellExactText(worksheet[addr], workbook);
                        if (exact !== "") {
                            row[phoneHeader] = exact;
                        }
                    }
                    let phone = normalizePhoneNumber(row[phoneHeader]);
                    const digitsOnly = phone.replace(/\D/g, "");

                    if (digitsOnly.length === 10) {
                        row[phoneHeader] = "1" + digitsOnly;
                    } else if (digitsOnly.length >= 11 && digitsOnly.length <= 15) {
                        row[phoneHeader] = digitsOnly;
                    } else if (digitsOnly.length > 0) {
                        row[phoneHeader] = digitsOnly;
                    } else {
                        row[phoneHeader] = phone;
                    }
                });
            }
            setUploadedFile(file);
            props?.HandleUploadFileData?.(file, jsonData);
        };
    };
    return (
        <div className={`w-p-100 h-p-100 `}>
            <div className='d-flex justify-center w-p-98 br-3 flex-direction m-auto my-auto border-primary-divider bg-color-secondary'>
                <div className='d-flex align-center flex-direction justify-center color-primary-accent bb-primary-divider-4 '
                    onDrop={(event) => {
                        event.preventDefault()
                        if (!uploadedFile?.name) {
                            const files = Array.from(event.dataTransfer.items).map((item) => item.getAsFile() as File)
                            files.forEach(async (file) => {
                                if (file !== null) {
                                    handleFileUpload(file)
                                }
                            })
                        }
                    }}
                    onDragOverCapture={(event) => {
                        event.preventDefault();
                    }}
                    onPaste={(event) => {
                        if (!uploadedFile?.name) {
                            const files = Array.from(event.clipboardData.items).map((item) => item.getAsFile() as File)
                            files.forEach(async (file) => {
                                if (file !== null) {
                                    handleFileUpload(file)
                                };
                            })
                        }
                    }}
                >
                    <div className='p-1 bg-color-consent-message-bg br-2 mt-5 mb-3'><UploadFileBlueIcon className='w-5 h-5' /></div>
                    {!uploadedFile?.name ? <>
                        <div className='h2-medium'>Drop your spreadsheet here</div>
                        <div className='h4-regular color-text-field-label'>or pick one from your computer</div>
                    </> : <div className=' h4-regular color-primary-light'>{uploadedFile?.name}</div>
                    }
                    {!!fileUploadError && (
                        <div className='h4-regular color-primary-danger'>{fileUploadError}</div>
                    )}
                    <div className='d-flex'>
                        {!!uploadedFile ?
                            <ButtonComponent ButtonLabel={'Clear File'} Variant={'danger-btn'} OnClick={() => handleClearFile()} /> :
                            <ButtonComponent ButtonLabel={'Choose File'} Variant={'primary-btn'} OnClick={() => handleOpenFileManager()} />}
                        <input
                            accept='.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            ref={fileInputRef}
                            type="file"
                            hidden
                            onChange={(e) => handleFileUpload(e?.target?.files?.[0])}
                        />
                    </div>
                    <div className='h6-regular color-text-field-label mb-2'>XLSX or CSV · up to 25 MB · up to 20,000 rows per file</div>
                </div>
                <div className='w-p-100 my-2'>
                    <div className='w-p-95 mx-auto h3-medium color-send-msg-title'>What happens after you upload</div>
                    <div className='w-p-95 mx-auto d-flex justify-between mt-1'>
                        <div className={`border-primary-divider w-p-30 pl-2 pr-1 py-1 br-2 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-message-log-bg"}`}>
                            <div className='d-flex align-center h4-medium color-primary-accent'>
                                <div className='bg-color-consent-message-bg px-1 br-1'><ContactMatchIcon /></div>
                                <span className='ml-1'> We match columns</span>
                            </div>
                            <div className='h5-regular color-text-field-label'>
                                First Name, Phone, Email — auto-detected.
                                Extra columns become attributes.
                            </div>
                        </div>

                        <div className={`border-primary-divider w-p-30 pl-2 pr-1 py-1 br-2 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-message-log-bg"}`}>
                            <div className='d-flex align-center h4-medium color-primary-accent'>
                                <div className='bg-color-consent-message-bg px-1 br-1'><TickBlueIcon /></div>
                                <span className='ml-1'>We validate phones</span>
                            </div>
                            <div className='h5-regular color-text-field-label'>
                                Format checked against E.164. We'll flag rows that need fixing.
                            </div>
                        </div>

                        <div className={`border-primary-divider w-p-30 pl-2 pr-1 py-1 br-2 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-message-log-bg"}`}>
                            <div className='d-flex align-center h4-medium color-primary-accent'>
                                <div className='bg-color-consent-message-bg px-1 br-1'> <MergeContactIcon /></div>
                                <span className='ml-1'>We find matches</span>
                            </div>
                            <div className='h5-regular color-text-field-label'>
                                Contacts already in Falkon are flagged so you can choose how to merge.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='d-flex justify-between w-p-98 mx-auto h5-regular color-primary-accent my-1'>
                <div>
                    <span>New to imports? Try our template: </span>
                    <span onClick={() => FileService.downloadWasabiFiles(Config.CONTACTS_TEMPLATE_URL_XLSX, "Contacts_Template.xlsx")} className="h5-regular color-primary-btn-focus c-pointer">contacts-template.xlsx</span> or
                    <span onClick={() => FileService.downloadWasabiFiles(Config.CONTACTS_TEMPLATE_URL_CSV, "Contacts_Template.csv")} className="h5-regular color-primary-btn-focus c-pointer"> contacts-template.csv</span>
                </div>
                <div className='d-flex align-center'>
                    <ShieldIcon className='mr-1' />Files are processed in your region. We don’t store originals.</div>
            </div>
        </div >
    );
};

export default ImportFile;